import { useState, useEffect, useCallback } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ExternalLink, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const loadProfiles = useCallback(async () => {
    try {
      const res = await api.get(`/admin/profiles?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`);
      setProfiles(res.data.profiles || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const toggleStatus = async (profileId, currentlyDeactivated) => {
    try {
      await api.put(`/admin/profiles/${profileId}/status`, { deactivated: !currentlyDeactivated });
      toast.success(currentlyDeactivated ? "Profile reactivated" : "Profile deactivated");
      loadProfiles();
    } catch {
      toast.error("Failed to update profile status");
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Profile Management</h1>
        <p className="text-muted-foreground text-sm">{total} total profiles</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by username or title..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="admin-profile-search"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-profiles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>QR Token</TableHead>
                <TableHead>Links</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : profiles.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No profiles found</TableCell></TableRow>
              ) : (
                profiles.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium text-sm font-mono">@{p.username}</TableCell>
                    <TableCell className="text-sm">{p.title || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.user_email || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">{p.qr_token || "—"}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{p.link_count || 0}</TableCell>
                    <TableCell>
                      <Badge className={p.deactivated ? "bg-red-500/10 text-red-400 border-red-500/20 text-xs" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs"}>
                        {p.deactivated ? "Deactivated" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <a href={`${BACKEND_URL}/p/${p.username}`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`admin-view-profile-${p.username}`}>
                            <ExternalLink size={14} />
                          </Button>
                        </a>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleStatus(p.id, p.deactivated)}
                          data-testid={`admin-toggle-profile-${p.username}`}
                        >
                          {p.deactivated ? <ToggleLeft size={14} className="text-red-400" /> : <ToggleRight size={14} className="text-emerald-400" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
